import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@bruno/ui';
import {
  deleteKnowledgeFile,
  listKnowledgeFiles,
  reprocessKnowledgeFile,
  uploadKnowledgeFile,
} from '../api/knowledge';

export function KnowledgeFilesPanel({ websiteId }: { websiteId: string }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, refetch } = useQuery({
    queryKey: ['knowledge', websiteId],
    queryFn: () => listKnowledgeFiles(websiteId),
    refetchInterval: (q) => {
      const files = q.state.data?.files ?? [];
      const anyProcessing = files.some((f) => f.status === 'pending' || f.status === 'processing');
      return anyProcessing ? 3000 : false;
    },
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadKnowledgeFile(websiteId, file),
    onSuccess: () => {
      void refetch();
      void qc.invalidateQueries({ queryKey: ['knowledge', websiteId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const remove = useMutation({
    mutationFn: (fileId: string) => deleteKnowledgeFile(websiteId, fileId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge', websiteId] }),
  });

  const reprocess = useMutation({
    mutationFn: (fileId: string) => reprocessKnowledgeFile(websiteId, fileId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge', websiteId] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">
          Upload PDFs, plain text, Markdown or HTML. Bruno will index them as a knowledge base.
        </p>
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,text/plain,text/markdown,text/html,.pdf,.txt,.md,.html"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) {
              setError(null);
              upload.mutate(f);
              e.target.value = '';
            }
          }}
        />
        <Button size="sm" onClick={() => fileInput.current?.click()} loading={upload.isPending}>
          + Upload file
        </Button>
      </div>
      {error ? <div className="text-sm text-red-600 mb-2">{error}</div> : null}
      {!data || data.files.length === 0 ? (
        <div className="text-sm text-slate-500 italic">No knowledge files yet.</div>
      ) : (
        <ul className="divide-y divide-slate-200">
          {data.files.map((f) => (
            <li key={f.id} className="py-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-medium truncate">{f.filename}</div>
                <div className="text-xs text-slate-500">
                  {(f.size / 1024).toFixed(1)} KB
                  {f.chunkCount !== null && f.chunkCount !== undefined ? ` · ${f.chunkCount} chunks` : ''}
                  {f.error ? ` · error: ${f.error}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <Badge tone={statusTone(f.status)}>{f.status}</Badge>
                {f.status === 'failed' ? (
                  <Button variant="ghost" size="sm" onClick={() => reprocess.mutate(f.id)}>
                    Retry
                  </Button>
                ) : null}
                <Button variant="ghost" size="sm" onClick={() => {
                  if (confirm('Delete this file?')) remove.mutate(f.id);
                }}>
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function statusTone(s: string): 'green' | 'amber' | 'red' | 'gray' | 'indigo' {
  switch (s) {
    case 'ready': return 'green';
    case 'processing': return 'indigo';
    case 'pending': return 'amber';
    case 'failed': return 'red';
    default: return 'gray';
  }
}
